/**
 * What the authoring assistant can be asked to do, and the offline router
 * that decides which it was.
 *
 * NINE ACTIONS, AND NO TENTH. The assistant cannot invent a tool, cannot
 * change a tool's physics, and cannot write a sentence about what a fab would
 * be like - every reply it gives is composed from the edit that actually
 * happened and from `flow/coverage.ts` running the product route over the
 * result. That is the same discipline the tool assistants keep (see
 * assistant/types.ts): the model chooses an action, the app performs it, and
 * the prose is derived from what came back.
 *
 * THE TRIGGERS ARE TRANSLATIONS. The tool assistants match English patterns
 * because their questions are physics questions in a panel that is already
 * English-labelled. This one is a conversation about a floor plan, and a
 * student reading the app in Chinese will type Chinese, so the verbs live in
 * the i18n catalogue - one `|`-separated list per action per language - and
 * the router matches whichever language is loaded. A catalogue with no list
 * for an action simply never routes to it offline, and the hosted router
 * still can.
 */

import { tOr } from '../../i18n/i18n'
import { normalise, phraseIndex } from './match'

export type AuthorActionId =
  /** Put a station, or a whole stage of the flow, on the floor. */
  | 'add'
  /** Take one off. */
  | 'remove'
  /** Replace the floor with a fab that exists. */
  | 'archetype'
  /** Walled bays, or an open ballroom. */
  | 'hall'
  /** Call it something. */
  | 'rename'
  /** Put the aisle back in the order a wafer meets the tools. */
  | 'sort'
  /** Strip it back to the airlock and start again. */
  | 'clear'
  /** What can this floor make, and what has it not got? Changes nothing. */
  | 'assess'
  /** What stations are there to choose from? Changes nothing. */
  | 'catalogue'

export const AUTHOR_ACTIONS: readonly AuthorActionId[] = [
  'archetype',
  'add',
  'remove',
  'assess',
  'hall',
  'sort',
  'clear',
  'rename',
  'catalogue',
]

/** True when the action only reads the floor. */
export const isQuestion = (id: AuthorActionId): boolean => id === 'assess' || id === 'catalogue'

/** One line for the model's catalogue and for the "what can I ask" list. */
export const summaryOf = (id: AuthorActionId): string => tOr(`author.action.${id}.Summary`, id)

/** Something a person might actually type, for the chips and for the model. */
export const exampleOf = (id: AuthorActionId): string => tOr(`author.action.${id}.Example`, '')

const triggersOf = (id: AuthorActionId): string[] =>
  tOr(`author.trigger.${id}`, '')
    .split('|')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)

/**
 * Every action a sentence could be asking for, likeliest first.
 *
 * EARLIEST VERB LEADS. "Take the crystal puller off and add an epi reactor"
 * is two requests and the first is the one the author will see happen and
 * can follow up on. Scoring by how often a verb appears would have answered
 * whichever was repeated, which is not a rule anybody types to.
 *
 * A RANKING RATHER THAN A CHOICE, because English verbs overlap: "put it in
 * flow order" leads with "put", which is an add, and there is no tool in the
 * sentence to add. The caller tries them in turn and keeps the first that
 * can actually do something, so a verb that cannot act steps aside for the
 * one behind it instead of producing a shrug. Ties go to whichever action
 * `AUTHOR_ACTIONS` lists first, which is why that list leads with the two
 * that replace a floor rather than nudge it.
 *
 * Empty when nothing matched. That is a real answer - it becomes the reply
 * listing what can be asked - and it is what the hosted router falls back to
 * when the network is not there.
 */
export function rankActionsLocally(text: string): AuthorActionId[] {
  const n = normalise(text)
  const scored: Array<{ id: AuthorActionId; at: number; rank: number }> = []
  AUTHOR_ACTIONS.forEach((id, rank) => {
    let earliest = -1
    for (const trigger of triggersOf(id)) {
      const at = phraseIndex(n, trigger)
      if (at >= 0 && (earliest < 0 || at < earliest)) earliest = at
    }
    if (earliest >= 0) scored.push({ id, at: earliest, rank })
  })
  return scored.sort((a, b) => a.at - b.at || a.rank - b.rank).map((s) => s.id)
}

/** The likeliest action, or null. */
export const routeActionLocally = (text: string): AuthorActionId | null =>
  rankActionsLocally(text)[0] ?? null
